/**
 * reconnectingEventSource — storm-safe EventSource with capped backoff.
 *
 * <h3>Why this exists (RC)</h3>
 * The browser's native EventSource reconnect fires FAST and UNCAPPED whenever a
 * stream errors while it can't reach the server (backend down / nginx fast-fails
 * with 502 — `readyState` stays CONNECTING). One stream doing that is a nuisance;
 * several always-on streams doing it across a tab is a request storm. A storm of
 * connection attempts overflows the server's TCP accept backlog, after which the
 * kernel REFUSES new connections (errno 111 "connection refused") — i.e. the
 * client storm can take the backend "down" without the backend ever crashing.
 *
 * Native EventSource only declines to auto-reconnect on a HARD close
 * (`readyState === CLOSED`, e.g. a 401/403). For everything else it hammers.
 *
 * <h3>What this does</h3>
 * Takes control of EVERY error: closes the stream itself (so the browser stops
 * its native retry) and reconnects on OUR OWN exponential backoff
 * (`baseDelayMs` → `maxDelayMs`) + jitter, never stacking timers. A clean open
 * resets the backoff. Worst case while the backend is unreachable is ~1 attempt
 * per `maxDelayMs` instead of a flood; a genuinely transient blip still recovers
 * in ~`baseDelayMs`.
 *
 * This is the SINGLE reconnect implementation — every SSE consumer should use it
 * rather than re-deriving backoff per file.
 *
 * <h3>Usage</h3>
 * Static (fixed event set):
 * {@code
 *   const conn = createReconnectingEventSource({
 *     url: '/api/v1/cloud-operations/events',
 *     events: { deployment_status: (ev) => {...} },
 *   });
 *   // on unmount: conn.close();
 * }
 * Dynamic (listeners come and go over one connection — e.g. a singleton bus):
 * {@code
 *   const conn = createReconnectingEventSource({ url, onOpen });
 *   conn.addEventListener('form_task_assigned', handler);   // persists across reconnects
 *   conn.removeEventListener('form_task_assigned', handler);
 * }
 *
 * A `url` function (instead of a string) is evaluated at each (re)connect, so
 * cursor / `?after=` semantics work — the next connect can carry an updated cursor.
 *
 * @param {Object} opts
 * @param {string|(() => string)|(() => Promise<string>)} opts.url  Stream URL,
 *        or a function returning it. The function may be ASYNC (return a
 *        Promise) for consumers that mint a fresh per-connect credential before
 *        each (re)connect (e.g. a single-use SSE stream token); a rejected/empty
 *        result is treated as a recoverable error and backs off + retries.
 * @param {Object<string, (ev: MessageEvent) => void>} [opts.events]  name → handler,
 *        pre-registered before the first connect.
 * @param {() => void} [opts.onOpen]  Called on each clean (re)connection.
 * @param {() => (void|Promise<void>)} [opts.recover]  Optional self-heal hook.
 *        Invoked ONCE before reconnecting when the stream HARD-closes
 *        (`readyState === CLOSED` — the signature of an HTTP-level rejection
 *        like 401/403, which the browser will not auto-reconnect). Use it to
 *        refresh an expired auth cookie. NOT called for transient/unreachable
 *        drops (`readyState === CONNECTING`), so a merely-down backend never
 *        hammers a refresh endpoint. Single-flight + capped (see
 *        `maxRecoverAttempts`) so a persistent rejection can't become a refresh
 *        loop. Omit it for today's exact behaviour (pure backoff).
 * @param {number}  [opts.maxRecoverAttempts=3]  Max consecutive `recover()`
 *        calls before falling back to plain backoff. Resets on a clean open.
 * @param {() => void} [opts.onError]  Called on EVERY stream error, after we
 *        take control of the close but before recover/backoff (not called on an
 *        intentional close()). Lets a consumer reflect a degraded state (e.g. a
 *        "connecting" status chip) without re-deriving error handling. Optional;
 *        omit for the exact prior behaviour.
 * @param {boolean} [opts.withCredentials=true]
 * @param {number}  [opts.baseDelayMs=1000]
 * @param {number}  [opts.maxDelayMs=30000]
 * @returns {{ addEventListener: Function, removeEventListener: Function, close: Function }}
 */
export function createReconnectingEventSource(opts) {
  const {
    url,
    events = {},
    onOpen,
    onError,
    recover,
    maxRecoverAttempts = 3,
    withCredentials = true,
    baseDelayMs = 1000,
    maxDelayMs = 30000,
  } = opts || {};

  /** Registered listeners, re-applied on every (re)connect: name → Set<handler>. */
  const registry = new Map();
  Object.entries(events).forEach(([name, handler]) => {
    if (typeof handler === 'function') {
      registry.set(name, new Set([handler]));
    }
  });

  let es = null;
  let attempts = 0;
  let timer = null;
  let closed = false;
  let recovering = false;     // single-flight guard for recover()
  let recoverAttempts = 0;    // capped per hard-close streak; reset on clean open

  const resolveUrl = () => (typeof url === 'function' ? url() : url);

  const applyListeners = () => {
    if (!es) return;
    for (const [name, handlers] of registry.entries()) {
      for (const fn of handlers) {
        try { es.addEventListener(name, fn); } catch { /* ignore */ }
      }
    }
  };

  const scheduleReconnect = () => {
    if (closed || timer != null) return;
    const delay = Math.min(maxDelayMs, baseDelayMs * (2 ** attempts))
                + Math.floor(Math.random() * 500);
    attempts += 1;
    timer = setTimeout(() => { timer = null; open(); }, delay);
  };

  // Some consumers must mint a fresh per-connect credential before each
  // (re)connect (e.g. an Auto-Create run's single-use 60s stream token, kept
  // out of the bearer header). For those, `url` is an async function: we await
  // it here and treat a rejection as a recoverable error (back off + retry),
  // exactly like a dropped stream — so a transient mint failure self-heals.
  // The SYNC path (url is a string or a sync function) stays fully synchronous:
  // no await, no microtask deferral, identical to prior behaviour.
  function open() {
    if (closed || es != null) return;
    let maybeUrl;
    try {
      maybeUrl = resolveUrl();
    } catch {
      if (!closed) { fireOnError(); scheduleReconnect(); }
      return;
    }
    if (maybeUrl && typeof maybeUrl.then === 'function') {
      maybeUrl
        .then((resolved) => startEventSource(resolved))
        .catch(() => { if (!closed) { fireOnError(); scheduleReconnect(); } });
      return;
    }
    startEventSource(maybeUrl);
  }

  const fireOnError = () => {
    if (typeof onError === 'function') {
      try { onError(); } catch { /* a bad status callback must not stop reconnect */ }
    }
  };

  function startEventSource(resolvedUrl) {
    // Re-check after any await: the consumer may have closed, or a prior
    // reconnect may already have a live stream, while the URL was resolving.
    if (closed || es != null) return;
    if (!resolvedUrl) { scheduleReconnect(); return; }
    let next;
    try {
      next = new EventSource(resolvedUrl, { withCredentials });
    } catch {
      // Browser without SSE support — give up quietly. Callers keep their
      // initial fetch / polling fallback, so this degrades gracefully.
      return;
    }
    es = next;
    es.onopen = () => {
      attempts = 0;
      recoverAttempts = 0;   // a clean open clears the hard-close streak
      if (typeof onOpen === 'function') onOpen();
    };
    es.onerror = () => {
      if (!es) return;
      // Capture the close class BEFORE we close it ourselves (close() flips
      // readyState to CLOSED). An ALREADY-CLOSED stream means the browser is
      // refusing to auto-reconnect — the signature of an HTTP-level rejection
      // (401/403). CONNECTING means transient / backend-unreachable.
      const hardClose = es.readyState === EventSource.CLOSED;
      try { es.close(); } catch { /* ignore */ }
      es = null;
      if (closed) return;
      // Surface the error to the consumer (degraded-state UI) before we
      // recover/back off.
      fireOnError();
      // Self-heal: on a hard close, give the caller ONE chance to recover
      // (e.g. refresh an expired auth cookie) before reconnecting. Single-flight
      // + capped so a persistent rejection can't become a refresh loop.
      // Transient/unreachable drops skip recovery (we never hammer a refresh
      // endpoint when the backend is merely down) and just back off — i.e. the
      // exact prior behaviour whenever no recover hook is supplied.
      if (hardClose && typeof recover === 'function'
          && !recovering && recoverAttempts < maxRecoverAttempts) {
        recovering = true;
        recoverAttempts += 1;
        Promise.resolve()
          .then(() => recover())
          .catch(() => { /* recovery failed — fall through to backoff */ })
          .finally(() => { recovering = false; if (!closed) scheduleReconnect(); });
        return;
      }
      scheduleReconnect();
    };
    applyListeners();
  }

  open();

  return {
    /** Register a named-event handler; persists across reconnects. */
    addEventListener(name, handler) {
      if (typeof handler !== 'function') return;
      let set = registry.get(name);
      if (!set) { set = new Set(); registry.set(name, set); }
      set.add(handler);
      if (es) { try { es.addEventListener(name, handler); } catch { /* ignore */ } }
    },
    /** Remove a previously-registered handler. */
    removeEventListener(name, handler) {
      const set = registry.get(name);
      if (!set) return;
      set.delete(handler);
      if (set.size === 0) registry.delete(name);
      if (es) { try { es.removeEventListener(name, handler); } catch { /* ignore */ } }
    },
    /** Tear down: cancel any pending reconnect and close the stream for good. */
    close() {
      closed = true;
      if (timer != null) { clearTimeout(timer); timer = null; }
      if (es) { try { es.close(); } catch { /* ignore */ } es = null; }
    },
  };
}

export default createReconnectingEventSource;
